import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { MdClose } from "react-icons/md";
import { useDispatch } from "react-redux";
import { NavLink, useLocation } from "react-router-dom";

import Logo from "../assets/blue_logo.svg";
import { ReactComponent as TeachersIcon } from "../assets/Group318.svg";
import { ReactComponent as StudentsIcon } from "../assets/Group320.svg";
import { ReactComponent as ClassroomIcon } from "../assets/Group322.svg";
import { ReactComponent as HomeIcon } from "../assets/Group324.svg";
import { ReactComponent as GeneralReportsIcon } from "../assets/Group326.svg";
import { ReactComponent as AdaptedIcon } from "../assets/Group330.svg";
import { ReactComponent as Top5Icon } from "../assets/Group335.svg";
import { ReactComponent as Top10Icon } from "../assets/Group337.svg";
import { ReactComponent as ImportStudentsIcon } from "../assets/importStudents.svg";
import { ReactComponent as LogoutIcon } from "../assets/logout_icon.svg";
import { ReactComponent as ContactIcon } from "../assets/Path103.svg";
import { ReactComponent as ClassroomReportsIcon } from "../assets/Subtraction3.svg";
import Breadcrumb from "../components/Breadcrumb";
import Button from "../components/Button";
import SchoolHeader from "../components/heading/SchoolHeader";
import ContactAccountManager from "../components/modals/school/ContactAccountManagerModal";
import { USER_TYPE } from "../constants/common";
import { useTheme } from "../context/ThemeContext";
import useAuth from "../hooks/useAuth";
import { clearUser } from "../redux/slices/userSlice";
import authServices from "../services/authServices";
import { getLanguage } from "../utils/getLanguage";
import { useNavigateWithLang } from "../utils/navigation";

const SchoolLayout = ({ children }) => {
  const user = useAuth();
  const [layout, setLayout] = useState("dense");
  const [isOpen, setIsOpen] = useState(true);
  const location = useLocation();
  const { theme } = useTheme();
  const { t } = useTranslation();
  const lang = getLanguage();
  const isRtl = lang === "ar";
  const isSmallScreen = window.innerWidth < 768; // Adjust based on your responsive design breakpoints

  const contactAccountManagerRef = useRef();

  const openContactAccountManagerModal = () => contactAccountManagerRef.current.openModal();

  const navigate = useNavigateWithLang();
  const dispatch = useDispatch();

  const handleSignOut = async () => {
    await authServices.logout();
    dispatch(clearUser());
    navigate(`/login`);
  };

  const toggleLayout = () => {
    setIsOpen(false);
    setLayout((prev) => (prev === "dense" ? "centered" : "dense"));
  };

  const toggleSidebar = () => {
    setIsOpen((prev) => !prev);
  };

  const closeSidebar = () => {
    if (isSmallScreen) {
      setIsOpen(false);
    }
  };

  const navItems = [
    {
      name: "teachers",
      icon: <TeachersIcon className="h-5 w-5" />,
      path: "teachers",
    },
    {
      name: "classrooms",
      icon: <ClassroomIcon className="h-5 w-5" />,
      path: "classrooms",
    },
    {
      name: "students",
      icon: <StudentsIcon className="h-5 w-5" />,
      path: "students",
    },
    {
      name: "import_students",
      icon: <ImportStudentsIcon className="h-5 w-5" />,
      path: "import-students",
    },
    { name: "line", path: "line" },
    {
      name: "general_reports",
      icon: <GeneralReportsIcon className="h-5 w-5" />,
      path: "general-reports",
    },
    {
      name: "classroom_reports",
      icon: <ClassroomReportsIcon className="h-5 w-5" />,
      path: "classroom-reports",
    },
    { name: "line", path: "line" },
    {
      name: "adapted_courses",
      icon: <AdaptedIcon className="h-5 w-5" />,
      path: "adapted-courses",
    },
    { name: "line", path: "line" },
    {
      name: "top_weekly_5_students",
      icon: <Top5Icon className="h-5 w-5" />,
      path: "top-weekly-5-students",
    },
    {
      name: "top_10_students",
      icon: <Top10Icon className="h-5 w-5" />,
      path: "top-10-students",
    },
    { name: "line", path: "line" },
    {
      name: "contact_account_manager",
      icon: <ContactIcon className="h-5 w-5" />,
      path: "button",
      onClick: openContactAccountManagerModal,
    },
    { name: "line", path: "line" },
    {
      name: "logout",
      icon: <LogoutIcon className="h-5 w-5" />,
      path: "button",
      onClick: handleSignOut,
    },
    {
      name: "",
      icon: "",
      path: "placeholder",
      onClick: () => {},
    },
  ];

  const SidebarItem = () => {
    return (
      <div className="group">
        <aside
          className={`fixed inset-y-0 px-1 shadow-md transition-transform duration-1000 ease-in-out group-hover:w-[18rem] group-hover:shadow-lg ${isRtl ? "right-0" : "left-0"} z-40 h-full transform bg-white text-text dark:bg-dark-surface dark:text-dark-text ${isOpen ? "w-[18rem] translate-x-0" : `w-20 ${isRtl ? "-translate-x-[-110%]" : "-translate-x-[110%]"}`} md:translate-x-0`}
          aria-label="Sidebar Navigation"
        >
          <div className="flex items-center rounded-md p-4 px-3 pb-0 capitalize">
            <div className="flex items-center space-x-4">
              <NavLink to={`/${lang}/${USER_TYPE.SCHOOL.ROLE}`}>
                <img src={Logo} alt="School" />
              </NavLink>
              <h1
                className={`mx-4 ${isOpen ? "" : "hidden group-hover:block"} text-xs text-gray-600 dark:text-white`}
              >
                <span className="max-w-full truncate font-semibold text-text dark:text-white">
                  {user?.school?.name}
                </span>
                <br />
                {USER_TYPE.SCHOOL.LABEL} Admin Dashboard
              </h1>
            </div>
            <Button
              variant="text"
              color="white"
              icon={<MdClose size={24} />}
              className="md:hidden"
              onClick={toggleSidebar}
              aria-label="Toggle Sidebar"
            />
          </div>

          {/* Make nav items scrollable */}
          <nav className="custom-scrollbar mt-6 h-[88%] overflow-y-auto">
            <ul className="space-y-0 px-1">
              <NavLink
                to={`/${lang}/${USER_TYPE.SCHOOL.ROLE}`}
                className={() => {
                  const isFirst = location.pathname.split("/").filter(Boolean).length == 2;
                  return `mb-1 flex items-center justify-center space-x-3 rounded-md px-3 py-[6px] text-sm leading-[21px] tracking-tight text-gray-400 transition duration-300 hover:bg-gray-50 hover:text-blue-500 dark:text-white dark:hover:bg-[#FAFBFC0D] ${isFirst ? "bg-gray-50 text-blue-500 dark:bg-[#253655]" : ""}`;
                }}
                onClick={closeSidebar} // Close sidebar on click
              >
                {() => {
                  const isFirst = location.pathname.split("/").filter(Boolean).length == 2;
                  return (
                    <>
                      <div
                        style={{
                          color: isFirst ? "#FF602B" : "#a4a5a7",
                          background: theme == "dark" ? "#1F2937" : "white",
                          borderRadius: "7px",
                          padding: "5px",
                        }}
                      >
                        <HomeIcon className="h-5 w-5" />
                      </div>
                      <p
                        className={`flex-grow transition-opacity duration-300 ease-in-out ${isOpen ? "" : "hidden group-hover:block"} ${isFirst ? "text-blue-500" : "text-gray-600 dark:text-white"}`}
                      >
                        {t(`dashboard`)}
                      </p>
                      {/* {isOpen &&
                        (isRtl ? (
                          <FaChevronLeft size={"10px"} className={`mx-2 ${isFirst ? "text-blue-500" : ""}`} />
                        ) : (
                          <FaChevronRight
                            size={"10px"}
                            className={`mx-2 ${isFirst ? "text-blue-500" : ""}`}
                          />
                        ))} */}
                    </>
                  );
                }}
              </NavLink>

              {navItems.map((item, index) => (
                <div key={index}>
                  {item.path === "placeholder" ? (
                    <div key={`line-${index}`} style={{ marginTop: "35px" }} />
                  ) : item.path === "line" ? (
                    <div
                      key={`line-${index}`}
                      style={{ marginBottom: "4px", marginTop: "4px" }}
                      className="mx-4 border-b-2 border-[#f0f0f0] dark:border-[#FAFBFC0D]"
                    />
                  ) : item.path === "button" ? (
                    <button
                      onClick={item.onClick}
                      key={index}
                      className={`flex w-full items-center rounded-md group-hover:justify-start ${isOpen ? "" : "justify-center"} space-x-2 px-4 py-2.5 text-sm leading-[21px] tracking-tight text-gray-400 transition duration-300 hover:bg-gray-50 hover:text-blue-500 dark:text-white dark:hover:bg-[#FAFBFC0D]`}
                    >
                      <div
                        style={{
                          color: "#a4a5a7",
                          background: theme == "dark" ? "#1F2937" : "white",
                          borderRadius: "7px",
                          padding: "5px",
                        }}
                      >
                        {item.icon}
                      </div>

                      <p
                        className={`text-sm tracking-tight ${isOpen ? "" : "hidden group-hover:block"} text-gray-600 dark:text-white`}
                      >
                        {t(`${item.name}`)}
                      </p>
                    </button>
                  ) : (
                    <li key={index}>
                      <NavLink
                        to={`/${lang}/${USER_TYPE.SCHOOL.ROLE}${item.path ? `/${item.path}` : ""}`}
                        className={({ isActive }) =>
                          `mb-1 flex items-center justify-center space-x-3 rounded-md px-3 py-[6px] text-sm leading-[21px] tracking-tight text-gray-300 transition duration-300 hover:bg-gray-50 hover:text-blue-500 dark:text-white ${item.path === "hellocode-courses" ? "hover:text-blue-500" : ""} dark:text-white dark:hover:bg-[#FAFBFC0D] ${isActive ? "bg-gray-50 text-blue-500 dark:bg-[#253655]" : ""}`
                        }
                        onClick={closeSidebar} // Close sidebar on click
                      >
                        {({ isActive }) => (
                          <>
                            <div
                              style={{
                                color: isActive ? "#FF602B" : "#a4a5a7",
                                background: theme == "dark" ? "#1F2937" : "white",
                                borderRadius: "7px",
                                padding: "5px",
                              }}
                            >
                              {item.icon}
                            </div>
                            <p
                              className={`flex-grow ${isOpen ? "" : "hidden group-hover:block"} ${isActive ? "text-blue-500" : "text-gray-600 dark:text-white"} `}
                            >
                              {t(`${item.name}`)}
                            </p>
                            {/* {isOpen &&
                              (isRtl ? (
                                <FaChevronLeft
                                  size={"10px"}
                                  className={`mx-2 ${isActive ? "text-blue-500" : ""}`}
                                />
                              ) : (
                                <FaChevronRight
                                  size={"10px"}
                                  className={`mx-2 ${isActive ? "text-blue-500" : ""}`}
                                />
                              ))} */}
                          </>
                        )}
                      </NavLink>
                    </li>
                  )}
                </div>
              ))}
            </ul>
          </nav>
        </aside>
      </div>
    );
  };

  return (
    <div className={`flex h-screen flex-col bg-[#E1E7EF] ${isRtl ? "rtl" : ""}`} dir={isRtl ? "rtl" : "ltr"}>
      <div
        className={`flex-1 ${layout == "centered" ? "m-10" : ""} rounded-2xl bg-background shadow-2xl dark:bg-dark-background`}
      >
        {/* Sidebar */}
        {layout == "dense" && <SidebarItem />}

        {/* Main Content Area */}
        <div
          className={`flex-1 bg-background transition-all duration-300 dark:bg-dark-background ${layout == "dense" ? (isOpen ? (isRtl ? "sm:mr-0 md:ml-0 md:mr-[18rem]" : "sm:ml-0 md:ml-[18rem] md:mr-0") : isRtl ? "sm:mr-0 md:ml-0 md:mr-20" : "sm:ml-0 md:ml-20 md:mr-0") : "mt-6"}`}
        >
          <SchoolHeader
            openContactAccountManagerModal={openContactAccountManagerModal}
            handleSignOut={handleSignOut}
            closeSidebar={closeSidebar}
            toggleSidebar={toggleSidebar}
            toggleLayout={toggleLayout}
            layout={layout}
            isOpen={isOpen}
          />

          <Breadcrumb />

          <main className="container mx-auto mb-6 px-4 dark:text-dark-text sm:px-8">{children}</main>
        </div>
        <ContactAccountManager ref={contactAccountManagerRef} />
      </div>
    </div>
  );
};

export default SchoolLayout;
